import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Link,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { fetchRepresentativeInfo } from "../../utils/representativeUtils";
import { theme } from "../../theme/colors";

export default function ContactInfoModal({
  open,
  onClose,
  bioguideId,
  memberName,
  memberRole,
}) {
  const [memberInfo, setMemberInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !bioguideId) return;

    setLoading(true);
    setError(null);
    setMemberInfo(null);

    fetchRepresentativeInfo(bioguideId)
      .then((data) => {
        setMemberInfo(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching member info:", err);
        setError(err?.message || "Failed to load contact information.");
        setLoading(false);
      });
  }, [open, bioguideId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: 1, borderColor: "divider", pb: 2 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, color: "#111" }}>
          Contact Information
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: "#111" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
            <CircularProgress size={32} sx={{ color: theme.primary }} />
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              Loading contact information…
            </Typography>
          </Box>
        ) : error && !memberInfo ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : memberInfo ? (
          <Stack spacing={2}>
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#111" }}>
                {memberInfo.first_name} {memberInfo.last_name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#000", mt: 0.25 }}>
                {memberInfo.party}
              </Typography>
            </Box>

            {memberInfo.office && memberInfo.office !== "Not available" && (
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <LocationOnOutlinedIcon sx={{ fontSize: 20, color: "text.secondary", mt: 0.25 }} />
                <Typography variant="body2" sx={{ color: "#111" }}>
                  Office: {memberInfo.office}
                </Typography>
              </Stack>
            )}

            {memberInfo.phone && memberInfo.phone !== "Not available" && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PhoneOutlinedIcon sx={{ fontSize: 20, color: theme.primary }} />
                <Link
                  href={`tel:${memberInfo.phone.replace(/[^\d+]/g, "")}`}
                  variant="body2"
                  sx={{ color: theme.primary, textDecoration: "underline", fontWeight: 600 }}
                >
                  {memberInfo.phone}
                </Link>
              </Stack>
            )}

            {memberInfo.contact_form && memberInfo.contact_form !== "Not available" && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <EmailOutlinedIcon sx={{ fontSize: 20, color: theme.primary }} />
                <Link
                  href={memberInfo.contact_form}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ color: theme.primary, textDecoration: "underline", fontWeight: 600 }}
                >
                  Contact Form
                </Link>
              </Stack>
            )}

            {memberInfo.official_website_url && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <LanguageIcon sx={{ fontSize: 20, color: theme.primary }} />
                <Link
                  href={memberInfo.official_website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ color: theme.primary, textDecoration: "underline", fontWeight: 600 }}
                >
                  Official Website
                </Link>
              </Stack>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
            Contact information not available.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
